import { IntlShape } from "react-intl";
import { getDurationInWords, parseDate } from "helpers/dateTimeHelper";

/** One ocean vessel leg: paired departure/arrival seaport rows sharing `stop_id` (see shipment schedule API contract). */
export type OceanVesselLeg = {
  stop_id: number;
  departure: any | null;
  arrival: any | null;
};

export type VesselScheduleSummary = {
  quoted_vessel_count?: number;
  current_vessel_count?: number;
  has_schedule_updates?: boolean;
  transit_time?: string | null;
};

export const getHeaderTitle = (freightModeType: string, intl: IntlShape) => {
  if (freightModeType === "air") {
    return intl.formatMessage({ id: "tracking.flightSchedule", defaultMessage: "Flight schedule" });
  }
  if (freightModeType === "ocean") {
    return intl.formatMessage({ id: "tracking.vesselSchedule", defaultMessage: "Vessel schedule" });
  }
  return intl.formatMessage({ id: "general.schedule", defaultMessage: "Schedule" });
};

export const getFlightScheduleDateTime = (isAirFreight: boolean, transitStop: any) => {
  const flightScheduleDate = isAirFreight
    ? transitStop.expected_stop_date
    : transitStop.updated_stop_date || transitStop.expected_stop_date;

  const flightScheduleTime = isAirFreight
    ? transitStop.expected_stop_time
    : transitStop.updated_stop_time || transitStop.expected_stop_time;

  return parseDate(`${flightScheduleDate} ${flightScheduleTime}`, "YYYY-MM-DD HH:mm:ss");
};

export const getUniqueStops = (transitStops: any) => {
  const uniqueAirports: any = {};
  const uniqueStops: any = [];
  transitStops.forEach((stop: any) => {
    if (!uniqueAirports[stop.airport]) {
      uniqueAirports[stop.airport] = true;
      uniqueStops.push(stop);
    }
  });
  return uniqueStops;
};

export const getStopsCodes = (isAirFreight: boolean, transitStops: any) =>
  transitStops.map((stop: any) => (isAirFreight ? stop.airport_code : stop.port_locode)).join(", ");

export const getTransportLocationName = (isAirFreight: boolean, transitStop: any, intl: IntlShape) => {
  const departFrom = intl.formatMessage({ id: "tracking.departFrom", defaultMessage: "Depart from" });
  const arriveAt = intl.formatMessage({ id: "tracking.arriveAt", defaultMessage: "Arrive at" });

  if (isAirFreight) {
    const prefix = transitStop.type === "flight_departure" ? departFrom : arriveAt;
    return `${prefix} ${transitStop.airport}${transitStop.airport_code ? ` (${transitStop.airport_code})` : ""}`;
  }
  const prefix = transitStop.type === "departure_seaport" ? departFrom : arriveAt;
  return `${prefix} ${transitStop.port_name}${transitStop.port_locode ? ` (${transitStop.port_locode})` : ""}`;
};

export const getTransitMessage = (currentStop: any, nextStop: any, intl: IntlShape): string => {
  const duration = getDurationInWords(
    getFlightScheduleDateTime(true, currentStop),
    getFlightScheduleDateTime(true, nextStop),
  );

  if (currentStop.airport !== nextStop.airport) {
    return `${duration} ${intl.formatMessage({
      id: "tracking.transitNextAirport",
      defaultMessage: "transit to next airport",
    })}`;
  }
  return `${duration} ${intl.formatMessage({ id: "tracking.layover", defaultMessage: "Layover" })} • ${currentStop.airport}${
    currentStop.airport_code ? ` (${currentStop.airport_code})` : ""
  }`;
};

const SEAPORT_DEPARTURE = "departure_seaport";
const SEAPORT_ARRIVAL = "arrival_seaport";

/**
 * Groups seaport schedule rows by `stop_id`, preserving first-seen leg order.
 */
export function groupOceanVesselLegs(transitStops: any[] | undefined): OceanVesselLeg[] {
  if (!transitStops?.length) return [];

  const seaportOnly = transitStops.filter((s) => s?.type === SEAPORT_DEPARTURE || s?.type === SEAPORT_ARRIVAL);

  const order: number[] = [];
  const byId = new Map<number, OceanVesselLeg>();

  seaportOnly.forEach((stop: any) => {
    const id = stop.stop_id;
    if (id == null) return;

    if (!byId.has(id)) {
      byId.set(id, { stop_id: id, departure: null, arrival: null });
      order.push(id);
    }
    const leg = byId.get(id)!;
    if (stop.type === SEAPORT_DEPARTURE) {
      leg.departure = stop;
    } else if (stop.type === SEAPORT_ARRIVAL) {
      leg.arrival = stop;
    }
  });

  return order.map((id) => byId.get(id)!);
}

/**
 * Human-readable duration from API `transit_time` (e.g. `"7d"`, `"31h"`).
 */
export function formatQuotedTransitTime(transitTime: string | null | undefined): string | null {
  if (transitTime == null || String(transitTime).trim() === "") return null;

  const m = String(transitTime)
    .trim()
    .match(/^(\d+)([dhms])$/i);
  if (!m) return transitTime;

  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();

  const label = (singular: string, plural: string) => (n === 1 ? `${n} ${singular}` : `${n} ${plural}`);

  switch (unit) {
    case "d":
      return label("day", "days");
    case "h":
      return label("hour", "hours");
    case "m":
      return label("minute", "minutes");
    case "s":
      return label("second", "seconds");
    default:
      return transitTime;
  }
}

/**
 * Line shown under the departure row for an ocean leg (vessel name + IMO).
 */
export function getOceanVesselCarrierLabel(departureStop: any): string | undefined {
  if (!departureStop) return undefined;

  const name = (departureStop.vessel_name || "").trim();
  const imo = (departureStop.vessel_number || "").trim();

  if (name && imo) {
    return `${name} • IMO ${imo}`;
  }
  if (imo) {
    return `IMO ${imo}`;
  }
  if (name) {
    return name;
  }
  return undefined;
}

/**
 * Whether to show the card-level "Updated" badge for ocean (prefers API summary flag).
 */
export function resolveOceanScheduleUpdated(
  vesselSummary: VesselScheduleSummary | null | undefined,
  isScheduleUpdatedFlag: boolean,
): boolean {
  if (vesselSummary?.has_schedule_updates != null) {
    return Boolean(vesselSummary.has_schedule_updates);
  }
  return isScheduleUpdatedFlag;
}
